import { NodeRequestConfig } from '../../../../ui/gzTree/uiElements/gzNodeRenameInput/helpers/types';
import { TreeNodeData } from '../../types';
import { ConsoleRequest, DataProviderType } from '../../../../utils/request';
import { BaseActionHandlers } from './base.action.handlers';
import { AsyncValidator, Validation, ValidatorType } from '../../../../ui/gzTree/type/validators';
import { CreateNetworkFolderAsyncValidator } from './create.network.folder.action.handlers';
import { CreateCompanyFolderAsyncValidator } from './create.company.folder.action.handlers';
import { GzToast, ToastSeverity } from '../../../../ui/gzToastNotification/type';
import { translateFn } from '../../../../utils/translator.helper';
import { GzToastNotificationWebComponent } from '../../../../ui/gzToastNotification';

export class RenameActionHandlers extends BaseActionHandlers {
  validator = {
    [ValidatorType.RENAME]: [new RenameAsyncValidator()],
  };

  public static isNetworkFolder(nodeType: number): boolean {
    const networkEntries = [
      Constants.ProtectedEntities.EPS.TYPE_CUSTOM_GROUP,
      Constants.ProtectedEntities.CONTAINERS.TYPE_FOLDER,
      Constants.ProtectedEntities.CONTAINERS.TYPE_HOST,
    ];

    const companyEntries = [Constants.Companies.TYPE_COMPANY_FOLDER];

    if (networkEntries.includes(nodeType)) {
      return true;
    } else if (companyEntries.includes(nodeType)) {
      return false;
    } else {
      throw new Error('Unable to rename node');
    }
  }

  protected async _createNodeRequest(nodeRequest: NodeRequestConfig): Promise<TreeNodeData> {
    nodeRequest.nodeData.originalData.name = nodeRequest.changes.name as string;
    const type = nodeRequest.nodeData.originalData.type;
    const isNetworkFolder = RenameActionHandlers.isNetworkFolder(type);

    const updateMethod = isNetworkFolder
      ? { endpoint: 'ProtectedEntitiesEPS', method: 'updateCustomGroups' }
      : { endpoint: 'Company', method: 'updateCompanyFolders' };

    const updateProtectedEntityRequest = {
      id: nodeRequest.nodeData.originalData._id,
      name: nodeRequest.nodeData.originalData.name,
      parentId: nodeRequest.nodeData.originalData.parentId,
      type: nodeRequest.nodeData.originalData.type,
    };

    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.EPS,
        endpoint: updateMethod.endpoint,
        method: updateMethod.method,
        payload: {
          records: [updateProtectedEntityRequest],
        },
      });

      const message = isNetworkFolder
        ? translateFn('Group successfully renamed.')
        : translateFn('Company folder successfully renamed.');
      const toastNotificationConfigs: GzToast = {
        message,
        hasAction: false,
        severity: ToastSeverity.SUCCESS,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);

      if (isNetworkFolder) {
        // Company folder rename action does not return the name from back-end, only network folders do
        nodeRequest.nodeData.originalData.name = response.data[nodeRequest.nodeData.nodeId].name;
      }
      nodeRequest.nodeData.nodeName = nodeRequest.nodeData.originalData.name;

      return nodeRequest.nodeData;
    } catch (exception) {
      const isDuplicateError = exception.err.message.includes('Name already exists in group');
      const message = isDuplicateError
        ? translateFn('Duplicate entry found. Unable to save changes.')
        : translateFn('An error has occurred. Please try again later.');
      const toastErrorConfig: GzToast = {
        message,
        hasAction: false,
        severity: ToastSeverity.ERROR,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastErrorConfig);
      throw new Error('Failed to create folder');
    }
  }
}

class RenameAsyncValidator extends AsyncValidator {
  public async validate(node: TreeNodeData, additionalData?: string): Promise<Validation> {
    const type = node.originalData.type;
    const isNetworkFolder = RenameActionHandlers.isNetworkFolder(type);

    return isNetworkFolder
      ? new CreateNetworkFolderAsyncValidator().validate(node, additionalData)
      : new CreateCompanyFolderAsyncValidator().validate(node, additionalData);
  }
}
